// src/DMCAGenerator.js
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usePlanTracker } from './hooks/usePlanTracker';
import useDMCAGenerator from './hooks/useDMCAGenerator';
import useFirestoreHistory from './hooks/useFirestoreHistory';
import { useNavigate } from 'react-router-dom';
import { DMCAForm } from './DMCAForm';
import { DMCAResults } from './DMCAResults';
import SidebarPrompt from './SidebarPrompt';
import './DMCAApp.css';
import AuthModal from './AuthModal';


const EMPTY_FORM = {
  claimantName: '',
  claimantEmail: '',
  claimantAddress: '',
  workType: 'Photograph / Image',
  workDescription: '',
  originalWorkUrl: '',
  infringingUrl: '',
  platform: '',
};

function DMCAGenerator() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const planTracker = usePlanTracker(user?.uid);
  const usageStats = planTracker?.getUsageStats();

  const {
    isProcessing,
    processingError,
    generatedLetters,
    aiResponse,
    generateDMCALetter,
    deleteLetter,
    clearAll,
    setGeneratedLetters,
  } = useDMCAGenerator(planTracker);

  const {
    chatHistory,
    isLoading: isHistoryLoading,
    saveChatSession,
    deleteChatSession,
    getRelativeTime,
  } = useFirestoreHistory();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, sessionId: null });

  const showSignInPrompt = !user && generatedLetters.length > 0;

  // Auto-save to history after generation
  useEffect(() => {
    if (generatedLetters.length > 0 && !currentSessionId && user && aiResponse) {
      const save = async () => {
        const title = `${formData.platform} — ${formData.workDescription}`;
        try {
          const sessionId = await saveChatSession(title, generatedLetters, aiResponse);
          if (sessionId) setCurrentSessionId(sessionId);
        } catch (_) {}
      };
      save();
    }
  }, [generatedLetters.length, aiResponse, currentSessionId, user]);

  const handleSubmit = async () => {
    if (!user) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }

    setHasGenerated(true);
    setCurrentSessionId(null);

    try {
      await generateDMCALetter(formData);
    } catch (_) {
      // error shown in form
    }
  };

  const handleNewNotice = () => {
    setFormData(EMPTY_FORM);
    setHasGenerated(false);
    setCurrentSessionId(null);
    clearAll();
  };

  const loadChatSession = (session) => {
    setHasGenerated(true);
    setCurrentSessionId(session.id);
    // session.qrCodes holds our letters (same history hook, same shape)
    const restored = (session.qrCodes || []).map(l => ({
      ...l,
      id: l.id || Date.now(),
    }));
    setGeneratedLetters(restored);
  };

  const handleDeleteSession = (sessionId, e) => {
    e.stopPropagation();
    setDeleteConfirmModal({ show: true, sessionId });
  };

  const confirmDelete = async () => {
    const { sessionId } = deleteConfirmModal;
    setDeleteConfirmModal({ show: false, sessionId: null });
    const ok = await deleteChatSession(sessionId);
    if (ok && currentSessionId === sessionId) handleNewNotice();
  };

  const cancelDelete = () => setDeleteConfirmModal({ show: false, sessionId: null });

  return (
    <div className="app-wrapper">
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onSwitchMode={setAuthMode}
      />

      {deleteConfirmModal.show && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-header"><h3>Delete notice</h3></div>
            <div className="delete-modal-body"><p>Are you sure you want to delete this notice from history?</p></div>
            <div className="delete-modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={cancelDelete}>Cancel</button>
              <button className="modal-btn modal-btn-delete" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <SidebarPrompt
        user={user}
        chatHistory={chatHistory}
        isHistoryLoading={isHistoryLoading}
        currentSessionId={currentSessionId}
        usageStats={usageStats}
        planTracker={planTracker}
        dynamicQRLimit={0}
        dynamicQRUsed={0}
        onNewChat={handleNewNotice}
        onLoadSession={loadChatSession}
        onDeleteSession={handleDeleteSession}
        onLogout={() => {}}
        getRelativeTime={getRelativeTime}
      />

      <div className={`app-container ${hasGenerated ? 'has-results' : ''} ${!user ? 'no-sidebar' : ''}`}>
        {!hasGenerated ? (
          <DMCAForm
            user={user}
            usageStats={usageStats}
            planTracker={planTracker}
            formData={formData}
            setFormData={setFormData}
            isProcessing={isProcessing}
            processingError={processingError}
            onSubmit={handleSubmit}
            showSignInPrompt={showSignInPrompt}
          />
        ) : (
          <DMCAResults
            isProcessing={isProcessing}
            generatedLetters={generatedLetters}
            user={user}
            deleteLetter={deleteLetter}
            onNewGeneration={handleNewNotice}
          />
        )}
      </div>
    </div>
  );
}

export default DMCAGenerator;